import { motion } from 'framer-motion';
import {
    Activity,
    Briefcase,
    Calendar,
    ClipboardList,
    FileText,
    LogOut,
    Menu,
    MessageCircle,
    Settings,
    UserPlus,
    Users,
    X,
    FileCheck,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { employerAPI } from '../../utils/api';

const navItems = [
  { name: 'Dashboard', icon: Activity, path: '/' },
  { name: 'Job Postings', icon: Briefcase, path: '/jobs' },
  { name: 'Candidates', icon: Users, path: '/candidates' },
  { name: 'Applications', icon: ClipboardList, path: '/applications' },
  { name: 'Forms', icon: FileCheck, path: '/forms' },
  { name: 'Chat', icon: MessageCircle, path: '/chat' },
  { name: 'Invoices', icon: FileText, path: '/invoices' },
  { name: 'Employees', icon: UserPlus, path: '/employees' },
  { name: 'Calendar', icon: Calendar, path: '/calendar' },
  { name: 'Settings', icon: Settings, path: '/settings' },
];

const Sidebar = ({ isMobile, toggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [companyData, setCompanyData] = useState({
    companyName: 'JobZentro',
    logo: null,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await employerAPI.getProfile();
        const profile = response.data.data;

        setCompanyData({
          companyName: profile.companyName || '',
          logo: profile.logo || null,
        });
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        toast.error('Failed to load profile data');
      }
    };

    fetchProfile();
  }, []);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const toggleCollapse = () => {
    if (!isMobile) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const sidebarVariants = {
    expanded: {
      width: '240px',
      transition: { duration: 0.3 }
    },
    collapsed: {
      width: '80px',
      transition: { duration: 0.3 }
    }
  };

  const logoVariants = {
    expanded: { opacity: 1, x: 0 },
    collapsed: { opacity: 0, x: -10 }
  };

  const textVariants = {
    expanded: {
      opacity: 1,
      x: 0,
      display: "block",
      transition: { 
        duration: 0.2,
        delay: 0.1
      }
    },
    collapsed: {
      opacity: 0,
      x: -10,
      transitionEnd: {
        display: "none"
      }
    }
  };

  return (
    <motion.aside 
      className={`flex flex-col h-full bg-white border-r border-gray-100 shadow-soft 
        ${isMobile ? 'fixed z-40 inset-y-0 left-0' : 'relative'}`}
      variants={sidebarVariants}
      initial={false}
      animate={isCollapsed && !isMobile ? 'collapsed' : 'expanded'}
    >
      {/* Mobile close button */}
      {isMobile && (
        <button 
          onClick={toggleSidebar}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <X size={20} />
        </button>
      )}

      {/* Logo */}
      <div className="flex items-center py-6 px-6 border-b border-gray-100">
        <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
        {companyData.logo && companyData.logo.url ? (
          <img
            src={`${process.env.REACT_APP_API_URL}${companyData.logo.url}`}
            alt="Company Logo"
            className="w-10 h-10 object-cover rounded-lg border-2 border-gray-200"
          />
        ) : (
          <span className="text-white text-lg font-logo font-bold">JH</span>
        )}
        </div>
        <motion.div 
          className="ml-3 font-logo font-semibold text-lg text-gray-900"
          variants={logoVariants}
        >
          {companyData.companyName}<span className="text-primary-600">.</span>
        </motion.div>
        
        {!isMobile && (
          <button 
            onClick={toggleCollapse} 
            className="ml-auto text-gray-500 hover:text-gray-700"
          >
            <Menu size={18} />
          </button>
        )}
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 py-6 px-4 overflow-y-auto">
        <ul className="space-y-2">
          {navItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            
            return (
              <li key={index}>
                <Link 
                  to={item.path}
                  onClick={isMobile ? toggleSidebar : undefined}
                  className={`flex items-center py-3 px-4 rounded-lg transition-all duration-200 ${
                    isActive 
                      ? 'bg-primary-50 text-primary-700' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <motion.div
                    initial={false}
                    animate={isActive ? { scale: 1.1 } : { scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <item.icon size={20} className={isActive ? 'text-primary-600' : ''} />
                  </motion.div>
                  <motion.span 
                    className={`ml-3 font-accent text-sm ${isActive ? 'font-medium' : ''}`}
                    variants={textVariants}
                  >
                    {item.name}
                  </motion.span>
                  
                  {isActive && (
                    <motion.div
                      className="absolute left-0 w-1 h-6 bg-primary-600 rounded-r-full"
                      layoutId="activeNavIndicator"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30
                      }}
                    />
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
      
      {/* Account section */}
      <div className={`px-4 py-5 border-t border-gray-100 ${isCollapsed && !isMobile ? 'flex justify-center' : 'mt-auto'}`}>
        <button 
          onClick={handleLogout}
          className="flex items-center py-3 px-4 text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-all duration-200 w-full"
        >
          <LogOut size={20} />
          <motion.span className="ml-3 text-sm font-accent" variants={textVariants}>
            Log Out
          </motion.span>
        </button>
      </div>
    </motion.aside>
  );
};

export default Sidebar; 